import {
  QueryAnalyticsBody,
  QueryAnalyticsResponse,
} from '@superfunnel/schemas';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../Firebase';

export async function requestAnalyticsUpdate(
  accountId: string,
  scope: 'account' | 'campaign',
  dynamicPageId?: string,
): Promise<any> {
  const fn = httpsCallable(functions, 'analytics/refresh');

  const response = await fn({
    accountId,
    scope,
    ...(dynamicPageId ? { campaignId: dynamicPageId } : {}),
  });

  return response.data;
}

export async function getAnalyticsByDates(
  accountId: string,
  scope: 'account' | 'campaign',
  dynamicPageId: string | undefined,
  startDate: string,
  endDate: string,
): Promise<any> {
  const fn = httpsCallable(functions, 'analytics/analyticsByDates');

  const response = await fn({
    accountId,
    scope,
    ...(dynamicPageId ? { campaignId: dynamicPageId } : {}),
    startDate,
    endDate,
  });

  return response.data;
}

export async function getWebsiteOnViewAnalytics(
  accountId: string,
  dynamicPageId: string,
  websiteId: string,
  startDate?: string,
  endDate?: string,
): Promise<any> {
  const fn = httpsCallable(functions, 'analytics/websiteOnViewAnalytics');

  const response = await fn({
    accountId,
    ...(dynamicPageId ? { campaignId: dynamicPageId } : {}),
    websiteId,
    startDate,
    endDate,
  });

  return response.data;
}

export async function getExternalAnalyticsByDates(
  accountId: string,
  dynamicPageId: string | undefined,
  startDate: string,
  endDate: string,
): Promise<any> {
  const fn = httpsCallable(functions, 'analytics/externalAnalyticsByDates');

  const response = await fn({
    accountId,
    ...(dynamicPageId ? { campaignId: dynamicPageId } : {}),
    startDate,
    endDate,
  });

  return response.data;
}

export type ExportFormat = 'csv' | 'json';

export async function exportAnalytics(
  accountId: string,
  filter: Record<string, any>,
  metrics: { value: string; title: string }[],
  groupBy: { value: string; title: string }[],
  segments: { value: string; title: string }[],
  startDate: string,
  endDate: string,
  format: ExportFormat = 'csv',
): Promise<{
  ok: boolean;
  jobId: string;
  fileName: string;
}> {
  const fn = httpsCallable<
    {
      accountId: string;
      filter: Record<string, any>;
      metrics: { value: string; title: string }[];
      groupBy: { value: string; title: string }[];
      segments: { value: string; title: string }[];
      startDate: string;
      endDate: string;
      format?: ExportFormat;
    },
    {
      ok: boolean;
      jobId: string;
      fileName: string;
    }
  >(functions, 'analytics/exportAnalytics');

  const response = await fn({
    accountId,
    filter,
    metrics,
    groupBy,
    segments,
    startDate,
    endDate,
    format,
  });

  return response.data;
}

export async function getUsage(
  accountId: string,
  startDate: string,
  endDate: string,
): Promise<{
  dailyVisitors: {
    event: string;
    day: string;
    custom: number;
    totalCustom: number;
    uniqueCustom: number;
  }[];
  totalVisitors: number;
}> {
  const fn = httpsCallable<
    {
      accountId: string;
      startDate: string;
      endDate: string;
    },
    {
      dailyVisitors: {
        event: string;
        day: string;
        custom: number;
        totalCustom: number;
        uniqueCustom: number;
      }[];
      totalVisitors: number;
    }
  >(functions, 'analytics/usage');

  const response = await fn({
    accountId,
    startDate,
    endDate,
  });

  return response.data;
}

export async function getAdsAccountsUsage(
  accountId: string,
  fromDate: string,
  toDate: string,
  aggregateBy: 'date' | 'total' = 'total',
  adsAccountsIds?: string[],
): Promise<
  {
    clicks: number;
    impressions: number;
    cost: number;
    conversions: number;
    isChargeable: boolean;
    adsAccountId: string;
    date?: string;
  }[]
> {
  const fn = httpsCallable<
    {
      accountId: string;
      fromDate: string;
      toDate: string;
      aggregateBy: 'date' | 'total';
      adsAccountsIds?: string[];
    },
    {
      clicks: number;
      impressions: number;
      cost: number;
      conversions: number;
      isChargeable: boolean;
      adsAccountId: string;
      date?: string;
    }[]
  >(functions, 'accounts/billing/getAdsAccountsUsage');

  const response = await fn({
    accountId,
    fromDate,
    toDate,
    aggregateBy,
    adsAccountsIds,
  });

  return response.data;
}

/**
 * Query analytics data with support for batching multiple queries.
 *
 * @example Single query
 * queryAnalytics({
 *   accountId,
 *   startDate: '2025-01-01',
 *   endDate: '2025-01-31',
 *   groupBy: ['day'],
 *   events: ['view', 'conversion']
 * })
 *
 * @example Batch with different date ranges
 * queryAnalytics({
 *   accountId,
 *   groupBy: ['day'],
 *   events: ['view'],
 *   queries: [
 *     { id: 'current', startDate: '2025-01-01', endDate: '2025-01-31' },
 *     { id: 'previous', startDate: '2024-12-01', endDate: '2024-12-31' }
 *   ]
 * })
 */
export async function queryAnalytics(
  params: QueryAnalyticsBody,
): Promise<QueryAnalyticsResponse> {
  const fn = httpsCallable<QueryAnalyticsBody, QueryAnalyticsResponse>(
    functions,
    'analytics/queryAnalytics',
  );

  const response = await fn(params);
  return response.data;
}

/**
 * Filter options response type
 */
export interface AnalyticsFilterOptionsResponse {
  os: { value: string; count: number }[];
  browser: { value: string; count: number }[];
  country: { value: string; count: number }[];
  city: { value: string; count: number }[];
  referrerDomain: { value: string; count: number }[];
  origin: { value: string; count: number }[];
  utmSource: { value: string; count: number }[];
  utmMedium: { value: string; count: number }[];
  botName: { value: string; count: number }[];
}

/**
 * Fetches distinct values for filter fields from analytics data.
 * Used to populate the filter builder dropdowns.
 */
export async function getAnalyticsFilterOptions(
  accountId: string,
  startDate: string,
  endDate: string,
): Promise<AnalyticsFilterOptionsResponse> {
  const fn = httpsCallable<
    {
      accountId: string;
      startDate: string;
      endDate: string;
    },
    AnalyticsFilterOptionsResponse
  >(functions, 'analytics/filterOptions');

  const response = await fn({
    accountId,
    startDate,
    endDate,
  });

  return response.data;
}
